import { cloneDeep } from 'lodash';
import { AxiosError } from 'axios';
import { ApiError } from '@/modules/core/app/models/api/ApiError';

const RESPONSE_TTL_MS = 60000;
/* eslint-disable tap/no-raw-text-js */
const PARAM_QUERY_ID = 'query_id';
const PARAM_WIDGET_REQUEST = 'widget_request';
const CANCELED_MESSAGE = 'canceled';
/* eslint-enable tap/no-raw-text-js */

/**
 * Shares one HTTP request among widgets issuing identical data queries
 * (in-flight de-dupe + short response TTL). Keys exclude per-request tracking
 * params; each consumer gets its own deep clone.
 */
export class WidgetQueryCache {
  static inflight = new Map();

  static responses = new Map();

  /**
   * @param {string} route
   * @param {Object} params
   * @returns {string}
   */
  static buildKey(route, params = {}) {
    const normalized = Object.keys(params)
      .filter((key) => key !== PARAM_QUERY_ID && key !== PARAM_WIDGET_REQUEST)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});
    normalized[PARAM_WIDGET_REQUEST] = PARAM_WIDGET_REQUEST in params;
    return `${route}::${JSON.stringify(normalized)}`;
  }

  /**
   * @param {string} key
   * @param {function(AbortSignal): Promise<*>} requestFactory
   * @param {?AbortSignal} signal abort signal of the calling widget
   * @param {boolean} skipCache bypass the response cache (still registers the result)
   * @returns {Promise<*>}
   */
  static async fetch(key, requestFactory, signal = null, skipCache = false) {
    if (!skipCache) {
      const cached = this.responses.get(key);
      if (cached && cached.expires_at > Date.now()) {
        return cloneDeep(cached.data);
      }
      this.responses.delete(key);
    }

    let entry = this.inflight.get(key);
    if (!entry) {
      const controller = new AbortController();
      entry = { controller, subscribers: 0 };
      entry.promise = Promise.resolve()
        .then(() => requestFactory(controller.signal))
        .then((data) => {
          this.responses.set(key, { data, expires_at: Date.now() + RESPONSE_TTL_MS });
          return data;
        })
        .finally(() => {
          if (this.inflight.get(key) === entry) {
            this.inflight.delete(key);
          }
        });
      this.inflight.set(key, entry);
    }

    return this._subscribe(entry, signal);
  }

  static deleteAll() {
    this.responses = new Map();
  }

  /**
   * Awaits the shared request; it aborts only once every subscriber has aborted.
   *
   * @param {Object} entry
   * @param {?AbortSignal} signal
   * @returns {Promise<*>}
   * @private
   */
  static _subscribe(entry, signal) {
    entry.subscribers += 1;

    if (!signal) {
      return entry.promise.then((data) => cloneDeep(data));
    }

    if (signal.aborted) {
      this._unsubscribe(entry);
      return Promise.reject(this._canceledError());
    }

    return new Promise((resolve, reject) => {
      const onAbort = () => {
        this._unsubscribe(entry);
        reject(this._canceledError());
      };
      signal.addEventListener('abort', onAbort, { once: true });
      entry.promise
        .then(
          (data) => resolve(cloneDeep(data)),
          (error) => reject(error)
        )
        .finally(() => signal.removeEventListener('abort', onAbort));
    });
  }

  /**
   * @param {Object} entry
   * @private
   */
  static _unsubscribe(entry) {
    entry.subscribers -= 1;
    if (entry.subscribers <= 0) {
      entry.controller.abort();
    }
  }

  /**
   * @returns {ApiError} same shape AxiosHelper produces for cancellations
   * @private
   */
  static _canceledError() {
    return new ApiError({
      status: AxiosError.ERR_CANCELED,
      message: CANCELED_MESSAGE,
    });
  }
}
